import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import * as moment from 'moment';
import { EmployeeService } from '../../employee/employee.service';

@Component({
  selector: 'app-pto-panel',
  templateUrl: './pto-panel.component.html',
  styleUrls: ['./pto-panel.component.css']
})
export class PtoPanelComponent implements OnChanges {

  // Manager whose reporting tree drives the "My team" filter. The panel always
  // loads every linked employee; this only decides which rows get flagged.
  @Input() employeeId: number;
  @Input() rangeDays = 14;
  @Input() title = 'Upcoming Time Off';

  // Optional date to highlight. The schedule page holds its selected date as
  // MM-DD-YYYY, so accept that as well as YYYY-MM-DD and normalise internally.
  @Input() focusDate: string;

  loading = false;
  loaded = false;
  error = '';
  entries: any[] = [];
  unlinked: string[] = [];
  range: any = null;
  hasTeam = false;
  teamOnly = false;
  regionFilter = '';

  constructor(private employeeService: EmployeeService) { }

  ngOnChanges(changes: SimpleChanges): void {
    // focusDate only drives the highlight, so changing it never refetches.
    if (changes.employeeId && !changes.employeeId.firstChange) {
      this.loaded = false;
      this.entries = [];
    }
  }

  // Filters only ever narrow the loaded list, so the backend's date ordering
  // is preserved as-is.
  get visibleEntries(): any[] {
    let list = this.teamOnly ? this.entries.filter(e => e.in_my_team) : this.entries;
    if (this.regionFilter) {
      list = list.filter(e => ((e.region || '') + '').trim() === this.regionFilter);
    }
    return list;
  }

  // Distinct regions present in the loaded window (region is trimmed server-side,
  // but trim again so a stray padded value can't split into two options).
  get regionOptions(): string[] {
    const regions: string[] = [];
    for (const entry of this.entries) {
      const region = ((entry.region || '') + '').trim();
      if (region && regions.indexOf(region) === -1) { regions.push(region); }
    }
    return regions.sort();
  }

  get unlinkedNames(): string {
    return this.unlinked.join(', ');
  }

  get normalizedFocusDate(): string {
    if (!this.focusDate) { return ''; }
    const parsed = moment(this.focusDate, ['YYYY-MM-DD', 'MM-DD-YYYY'], true);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
  }

  // True when focusDate falls outside the loaded window, so the template can offer
  // a reload instead of an empty table that reads as "nobody is off".
  get focusOutOfRange(): boolean {
    const focus = this.normalizedFocusDate;
    if (!focus || !this.range) { return false; }
    return focus < this.range.start || focus > this.range.end;
  }

  isFocused(entry: any): boolean {
    const focus = this.normalizedFocusDate;
    if (!focus) { return false; }
    return entry.start <= focus && entry.end >= focus;
  }

  // Called from the panel's (opened) handler only — never ngOnInit. Loading on
  // expand rather than page load is the point of the feature.
  load(force = false): void {
    if (this.loading || (this.loaded && !force)) { return; }
    this.fetch(moment().format('YYYY-MM-DD'));
  }

  // Reload centred on focusDate when the user has navigated outside the window.
  loadForFocusDate(): void {
    const focus = this.normalizedFocusDate;
    if (focus) { this.fetch(focus); }
  }

  refresh(): void {
    this.load(true);
  }

  formatRange(entry: any): string {
    const from = moment(entry.start, 'YYYY-MM-DD').format('M/D');
    const to = moment(entry.end, 'YYYY-MM-DD').format('M/D');
    return from === to ? from : from + ' – ' + to;
  }

  private fetch(start: string): void {
    if (!this.employeeId) { return; }
    const end = moment(start, 'YYYY-MM-DD').add(this.rangeDays, 'days').format('YYYY-MM-DD');

    this.loading = true;
    this.error = '';

    this.employeeService.getTeamTimeOff(this.employeeId, start, end).subscribe(
      res => {
        this.loading = false;
        if (!res || res.Error) {
          this.error = (res && res.Message) ? res.Message : 'Could not load time off.';
          return;
        }
        this.entries = res.Data || [];
        this.unlinked = res.Unlinked || [];
        this.range = res.Range || null;
        this.hasTeam = res.HasTeam === true;
        if (!this.hasTeam) { this.teamOnly = false; }
        // A refetch can change which regions exist; a stale selection would
        // render an empty table that reads as "nobody is off".
        if (this.regionFilter && this.regionOptions.indexOf(this.regionFilter) === -1) {
          this.regionFilter = '';
        }
        this.loaded = true;
      },
      () => {
        this.loading = false;
        this.error = 'Could not reach the server to load time off.';
      }
    );
  }
}
